import React, { useMemo, useCallback } from 'react'
import _ from 'lodash'
import cx from 'classnames'

import S from './styled'
import { servings }  from '../../../../common/recipe'

import {
  Input,
  TranslatedInput,
  Checkbox,
  Select,
  TextArea,
} from  '../../../../components'

import { RefreshIcon } from '../../../../components/icons'

import { maxValue, required } from '../../../../common/validators'

import { getServingsInfo } from '../utils'

const maxValueValidator = maxValue(1000000)

const isExludedServing = (selectedServings, serving) => !selectedServings.includes(serving)

const quantityValidator = [required, maxValueValidator]

const IngredientDetails = ({
  selectedIngredient,
  handleTranslationChange,
  handleRecalculateQuantity,
  ingredientModel,
  locales, defaultLocale,
  servings: selectedServings,
  ingredients,
  regions: regionsList,
  field: variationField,
  units: availableUnits,
  locked,
}) => {

  const { model, checkbox, text, number, select, onChange } = ingredientModel

  const ingredientPrefix = useMemo(
    () => path => `values[${variationField}].${selectedIngredient}.${path}`,
    [selectedIngredient, variationField],
  )

  const unitsList = useMemo(() => availableUnits.map(
    unit => ({
      id: unit.id,
      name: unit.displayName[defaultLocale],
    }),
  ), [availableUnits, defaultLocale])

  const {
    defaultServing,
    step,
    unitID,
  } = getServingsInfo(selectedServings, _.get(model, ingredientPrefix('servings')), unitsList)

  const originalIngredient = useMemo(() => {
    const id = _.get(model, `values[${variationField}].${selectedIngredient}`, {}).ingredientID
    return ingredients.find(i => i.id === id)
  }, [ingredients, model, selectedIngredient, variationField])

  const regions = useMemo(() => {
    const originalRegions = _.get(originalIngredient, 'availableRegions', [])
    return originalRegions.map(id => regionsList.find(region => region.id === id).description).join(', ')
  }, [originalIngredient, regionsList])

  const ingredient = _.get(model, `values[${variationField}].${selectedIngredient}`, {})
  const toggleServings = useCallback( e => {
    onChange({
      target: {
        name: `values[${variationField}].${selectedIngredient}.servings`,
        value: e.target.value ? selectedServings.reduce((acc, value) => {
          return {
            ...acc,
            [value]: { quantity: 0, unitID: availableUnits[0].id },
          }
        }, {}) : undefined,
      },
    })
  }, [availableUnits, onChange, selectedIngredient, selectedServings, variationField])

  const hasServings = !!ingredient.servings

  return (
    <S.IngredientDetails>
      {
        selectedIngredient ? (
          <React.Fragment>
            <S.IngredientDescriptionBlock>
              <S.IngredientDetailsRow>
                <div>
                  {
                    !selectedIngredient.includes('replacements') && (
                      <React.Fragment>
                        <div>
                          <Checkbox {...checkbox({
                            name: ingredientPrefix`isOptional`,
                            title: 'Is Optional',
                          })} readOnly={locked}/>
                        </div>
                        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                          <Checkbox {...checkbox({
                            name: ingredientPrefix`hasReplacement`,
                            title: 'Has Replacements',
                          })} readOnly={locked}/>
                        </div>
                      </React.Fragment>
                    )
                  }
                </div>
                <div>
                  <div>
                    <Checkbox {...checkbox({
                      name: ingredientPrefix`servings`,
                      title: 'Has servings',
                      value: hasServings,
                      onChange: toggleServings,
                    })} readOnly={locked}/>
                  </div>
                </div>
              </S.IngredientDetailsRow>
              <S.IngredientDetailsRow>
                <TranslatedInput {...{
                  name: ingredientPrefix(`additionalDescription.${defaultLocale}`),
                  text,
                  title: 'Additional Description',
                  locales,
                  defaultLocale,
                  translatedField: ingredientPrefix`additionalDescription`,
                  translates: _.get(model, ingredientPrefix`additionalDescription`, {}),
                  onTranslationChange: handleTranslationChange,
                  inverted: true,
                  className: 'descriptionInput',
                  disabled: locked,
                  optional: true,
                }}/>
              </S.IngredientDetailsRow>
              <S.IngredientDetailsRow>
                <TextArea {...{
                  name: ingredientPrefix`region`,
                  text,
                  title: 'Regional Availability',
                  inverted: true,
                  className: 'descriptionInput',
                  rows: 8,
                  disabled: locked,
                }}
                value={regions}
                readOnly
                />
              </S.IngredientDetailsRow>
            </S.IngredientDescriptionBlock>
            {
              hasServings ? (
                <S.IngredientQuantityBlock>
                  <S.Label>Amount per Servings</S.Label>
                  <div>
                    <S.Refresh
                      onClick={locked ? null : handleRecalculateQuantity}
                      className={locked ? 'locked' : ''}
                    >
                      <RefreshIcon />
                    </S.Refresh>
                  </div>
                  {
                    servings.map((serving, index) => {
                      const excluded = isExludedServing(selectedServings, serving)
                      return (
                        <S.ServingRow key={`serving_${serving}`} className={cx({
                          first: index === 0,
                        })}>
                          <S.Serving className={cx({
                            selected: serving === defaultServing,
                            first: index === 0,
                            disabled: excluded || locked,
                          })}>{serving}</S.Serving>
                          <Input {...{
                            name: ingredientPrefix(`servings.${serving}.quantity`), number,
                            title: index === 0 ? 'Quantity' : '',
                            validate: quantityValidator,
                            min: 0,
                            defaultValue: step * serving,
                            inverted: true,
                            disabled: excluded || locked,
                            ...(excluded ? {
                              value: step * serving,
                            } : {}),
                          }}
                          />
                          <Select {...{
                            name: ingredientPrefix(`servings.${serving}.unitID`),
                            select,
                            title: index === 0 ? 'Units' : '',
                            values: unitsList,
                            validate: required,
                            defaultValue: unitID,
                            inverted: true,
                            disabled: excluded || locked,
                            noEmpty: true,
                            ...(excluded ? {
                              value: unitID,
                            } : {}),
                          }}
                          />
                        </S.ServingRow>
                      )
                    })
                  }
                </S.IngredientQuantityBlock>
              ) : <S.IngredientQuantityBlockPlaceholder />
            }
          </React.Fragment>
        ) : <S.Placeholder>Please select ingredient to customize</S.Placeholder>
      }
    </S.IngredientDetails>
  )
}

export default IngredientDetails
